import React, { useState, useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import DatePickerInput from '../datePicker/datePicker';
import FormInput from '../formInput/formInput';
import SelectField from '../selectInput/select';

const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1 MB

const compressImage = async (canvas, quality = 0.7) => {
    return new Promise((resolve) => {
        canvas.toBlob(
            (blob) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.readAsDataURL(blob);
            },
            'image/jpeg',
            quality
        );
    });
};

const CameraCapture = ({ onCapture, onClose, imageType }) => {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);
    const [capturedImage, setCapturedImage] = useState(null);

    useEffect(() => {
        const startCamera = async () => {
            try {
                const mediaStream = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: 'environment' }
                });
                streamRef.current = mediaStream;
                if (videoRef.current) {
                    videoRef.current.srcObject = mediaStream;
                }
            } catch (error) {
                Swal.fire({
                    icon: 'error',
                    title: 'Camera Access Denied',
                    text: 'Unable to access camera. Please check permissions.'
                });
                onClose();
            }
        };

        startCamera();

        return () => {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
            }
        };
    }, [onClose]);

    const capturePhoto = () => {
        if (videoRef.current && canvasRef.current) {
            const context = canvasRef.current.getContext('2d');
            canvasRef.current.width = videoRef.current.videoWidth;
            canvasRef.current.height = videoRef.current.videoHeight;
            context.drawImage(videoRef.current, 0, 0);
            const imageData = canvasRef.current.toDataURL('image/jpeg');
            setCapturedImage(imageData);
        }
    };

    const retakePhoto = async () => {
        setCapturedImage(null);
        try {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
            }
            const mediaStream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: 'environment' }
            });
            streamRef.current = mediaStream;
            if (videoRef.current) {
                videoRef.current.srcObject = mediaStream;
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Camera Error',
                text: 'Failed to restart camera.'
            });
        }
    };

    const handleClose = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
        }
        onClose();
    };

    const acceptPhoto = async () => {
        if (capturedImage) {
            try {
                let compressedImage = capturedImage;
                let quality = 0.7;

                while (compressedImage.length > MAX_FILE_SIZE && quality > 0.1) {
                    quality -= 0.1;
                    const canvas = document.createElement('canvas');
                    const img = new Image();
                    img.src = capturedImage;
                    await new Promise(resolve => {
                        img.onload = resolve;
                    });
                    canvas.width = img.width;
                    canvas.height = img.height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0);
                    compressedImage = await compressImage(canvas, quality);
                }

                onCapture(compressedImage);
                if (streamRef.current) {
                    streamRef.current.getTracks().forEach(track => track.stop());
                }
                onClose();
            } catch (error) {
                Swal.fire({
                    icon: 'error',
                    title: 'Compression Failed',
                    text: 'Failed to process image.'
                });
            }
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
                <h3 className="text-xl font-bold mb-4">Capture {imageType}</h3>

                {!capturedImage ? (
                    <div className="space-y-4">
                        <video
                            ref={videoRef}
                            autoPlay
                            playsInline
                            muted
                            className="w-full rounded-lg bg-black"
                        />
                        <canvas ref={canvasRef} style={{ display: 'none' }} />
                        <div className="flex gap-3">
                            <button
                                onClick={capturePhoto}
                                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg"
                            >
                                Capture
                            </button>
                            <button
                                onClick={handleClose}
                                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded-lg"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <img src={capturedImage} alt="Captured" className="w-full rounded-lg" />
                        <div className="flex gap-3">
                            <button
                                onClick={acceptPhoto}
                                className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg"
                            >
                                Use Photo
                            </button>
                            <button
                                onClick={retakePhoto}
                                className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded-lg"
                            >
                                Retake
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const Insurance = ({ insuranceData = [], onInsurancesChange, onFormStateChange, isNext }) => {
    const [insurances, setInsurances] = useState(
        insuranceData.map(item => ({
            ...item,
            is_new: item.is_new ?? false
        }))
    );
    const isFirstMount = useRef(true);

    const onInsurancesChangeRef = useRef(onInsurancesChange);
    const onFormStateChangeRef = useRef(onFormStateChange);

    useEffect(() => {
        onInsurancesChangeRef.current = onInsurancesChange;
        onFormStateChangeRef.current = onFormStateChange;
    }, [onInsurancesChange, onFormStateChange]);

    useEffect(() => {
        if (isFirstMount.current) {
            isFirstMount.current = false;
            return;
        }
        if (onInsurancesChangeRef.current) {
            onInsurancesChangeRef.current(insurances);
        }
    }, [insurances]);
    const [showForm, setShowForm] = useState(false);
    const [editingIndex, setEditingIndex] = useState(null);

    useEffect(() => {
        if (onFormStateChangeRef.current) {
            const formIsOpen = showForm || editingIndex !== null;
            onFormStateChangeRef.current(formIsOpen);
        }
    }, [showForm, editingIndex]);
    const [cameraMode, setCameraMode] = useState(null);
    const [pendingImageType, setPendingImageType] = useState(null);
    const [newInsurance, setNewInsurance] = useState({
        insurancepolicyholder: '',
        insureddob: '',
        insuredsex: '',
        relationshiptoinsured: '',
        insuranceplanname: '',
        insuranceidnumber: '',
        frontimage: '',
        backimage: '',
        is_new: true
    });

    const handleDelete = (index) => {
        Swal.fire({
            title: 'Delete Insurance?',
            text: 'Are you sure you want to delete this insurance?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel'
        }).then((result) => {
            if (result.isConfirmed) {
                if (insurances[index].is_new) {
                    setInsurances(insurances.filter((_, i) => i !== index));
                } else {
                    const updated = [...insurances];
                    updated[index] = { ...updated[index], isDeleted: true };
                    setInsurances(updated);
                }
                Swal.fire({
                    icon: 'success',
                    title: 'Deleted!',
                    text: 'Insurance has been deleted successfully.',
                    timer: 2000,
                    showConfirmButton: false
                });
            }
        });
    };

    const handleUndo = (index) => {
        const updated = [...insurances];
        updated[index] = { ...updated[index], isDeleted: false };
        setInsurances(updated);
    };

    const handleEditChange = (e, index) => {
        const { name, value } = e.target;
        const updated = [...insurances];
        updated[index] = { ...updated[index], [name]: value };
        setInsurances(updated);
    };

    const handleEditCameraCapture = (index, imageType) => {
        setCameraMode(`edit-${index}-${imageType}`);
        setPendingImageType(imageType);
    };

    const handleEditCaptureComplete = (index, imageData) => {
        const updated = [...insurances];
        updated[index] = { ...updated[index], [pendingImageType]: imageData };
        setInsurances(updated);
        setCameraMode(null);
        setPendingImageType(null);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';

        // If it's already formatted as MM/DD/YYYY, return as-is
        if (dateStr.includes('/')) {
            return dateStr;
        }

        // If it's YYYY-MM-DD format, reformat
        if (dateStr.includes('-')) {
            const [year, month, day] = dateStr.split('-');
            return `${month}/${day}/${year}`;
        }

        return 'N/A';
    };

    const sanitizeInsuranceId = (id) => {
        // Remove all hyphens and special characters, keep only alphanumeric
        return id?.trim().replace(/[^a-zA-Z0-9]/g, '').toUpperCase() || '';
    };

    const validateInsurance = (data, excludeIndex = null) => {
        const sanitizedId = sanitizeInsuranceId(data.insuranceidnumber);

        if (!sanitizedId) {
            Swal.fire({
                icon: 'warning',
                title: 'Required Field',
                text: 'Insurance ID Number is required',
            });
            return false;
        }

        // Check for duplicate insurance ID
        const isDuplicate = insurances.some((insurance, index) =>
            sanitizeInsuranceId(insurance.insuranceidnumber) === sanitizedId &&
            index !== excludeIndex &&
            !insurance.isDeleted
        );

        if (isDuplicate) {
            Swal.fire({
                icon: 'error',
                title: 'Duplicate Insurance ID',
                text: 'This insurance ID number already exists. Please use a different ID.',
            });
            return false;
        }

        if (!data.insurancepolicyholder?.trim()) {
            Swal.fire({
                icon: 'warning',
                title: 'Required Field',
                text: 'Policy Holder Name is required',
            });
            return false;
        }
        if (!data.insureddob?.trim()) {
            Swal.fire({
                icon: 'warning',
                title: 'Required Field',
                text: 'Date of Birth is required',
            });
            return false;
        }
        if (!data.insuredsex?.trim()) {
            Swal.fire({
                icon: 'warning',
                title: 'Required Field',
                text: 'Gender is required',
            });
            return false;
        }
        if (!data.relationshiptoinsured?.trim()) {
            Swal.fire({
                icon: 'warning',
                title: 'Required Field',
                text: 'Relationship is required',
            });
            return false;
        }
        if (!data.insuranceplanname?.trim()) {
            Swal.fire({
                icon: 'warning',
                title: 'Required Field',
                text: 'Insurance Plan Name is required',
            });
            return false;
        }
        return true;
    };

    const handleSaveEdit = () => {
        if (validateInsurance(insurances[editingIndex], editingIndex)) {
            const updated = [...insurances];
            updated[editingIndex] = {
                ...updated[editingIndex],
                insuranceidnumber: sanitizeInsuranceId(updated[editingIndex].insuranceidnumber)
            };
            setInsurances(updated);
            setEditingIndex(null);
        }
    };

    const handleAddNew = () => {
        setShowForm(true);
    };

    const handleCameraCapture = (imageType) => {
        setCameraMode(imageType);
        setPendingImageType(imageType);
    };

    const handleCaptureComplete = (imageData) => {
        setNewInsurance(prev => ({
            ...prev,
            [pendingImageType]: imageData
        }));
        setCameraMode(null);
        setPendingImageType(null);
    };

    const handleSaveInsurance = () => {
        if (validateInsurance(newInsurance)) {
            const sanitized = {
                ...newInsurance,
                insuranceidnumber: sanitizeInsuranceId(newInsurance.insuranceidnumber)
            };
            setInsurances([...insurances, sanitized]);
            setNewInsurance({
                insurancepolicyholder: '',
                insureddob: '',
                insuredsex: '',
                relationshiptoinsured: '',
                insuranceplanname: '',
                insuranceidnumber: '',
                frontimage: '',
                backimage: '',
                is_new: true
            });
            setShowForm(false);
        }
    };


    return (
        <div className="w-full p-6">
            {!isNext && (
                <div className="mb-6">
                    <div className="mb-4 p-4 bg-blue-50 border-l-4 border-blue-600 rounded">
                        <p className="text-blue-800 font-semibold">
                            <i className="fas fa-info-circle mr-2"></i>
                            Please confirm your insurance details
                        </p>
                    </div>
                    <button
                        onClick={handleAddNew}
                        type="button"
                        className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg shadow-md flex items-center gap-2"
                    >
                        <i className="fas fa-plus"></i>
                        Add New Insurance
                    </button>
                </div>
            )}

            {/* Add New Insurance Form */}
            {showForm && (
                <div className="mb-8 p-6 bg-white border-2 border-blue-300 rounded-lg">
                    <h3 className="text-xl font-bold mb-4 text-gray-800">Add New Insurance</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <FormInput
                            id="policy-holder-new"
                            label="Policy Holder Name"
                            value={newInsurance.insurancepolicyholder}
                            onChange={(e) => setNewInsurance(prev => ({ ...prev, insurancepolicyholder: e.target.value }))}
                            placeholder="Enter policy holder name"
                            required
                            labelPosition="top"
                        />
                        <DatePickerInput
                            id="dob-new"
                            label="Date of Birth"
                            value={newInsurance.insureddob ? new Date(newInsurance.insureddob) : null}
                            onChange={(date) => {
                                const dateString = date ? date.toISOString().split('T')[0] : '';
                                setNewInsurance(prev => ({ ...prev, insureddob: dateString }));
                            }}
                            required
                        />
                        <SelectField
                            id="gender-new"
                            label="Gender"
                            options={[
                                { value: 'M', label: 'Male' },
                                { value: 'F', label: 'Female' },
                                { value: 'Other', label: 'Other' }
                            ]}
                            value={newInsurance.insuredsex}
                            onChange={(e) => setNewInsurance(prev => ({ ...prev, insuredsex: e.target.value }))}
                            required
                            labelPosition="top"
                        />
                        <SelectField
                            id="relationship-new"
                            label="Relationship"
                            options={[
                                { value: 'Self', label: 'Self' },
                                { value: 'Spouse', label: 'Spouse' },
                                { value: 'Child', label: 'Child' },
                                { value: 'Parent', label: 'Parent' },
                                { value: 'Other', label: 'Other' }
                            ]}
                            value={newInsurance.relationshiptoinsured}
                            onChange={(e) => setNewInsurance(prev => ({ ...prev, relationshiptoinsured: e.target.value }))}
                            required
                            labelPosition="top"
                        />
                        <FormInput
                            id="plan-name-new"
                            label="Insurance Plan Name"
                            value={newInsurance.insuranceplanname}
                            onChange={(e) => setNewInsurance(prev => ({ ...prev, insuranceplanname: e.target.value }))}
                            placeholder="Enter insurance plan name"
                            required
                            labelPosition="top"
                        />
                        <FormInput
                            id="id-number-new"
                            label="Insurance ID Number"
                            value={newInsurance.insuranceidnumber}
                            onChange={(e) => setNewInsurance(prev => ({ ...prev, insuranceidnumber: e.target.value }))}
                            placeholder="Enter insurance ID number"
                            required
                            labelPosition="top"
                        />
                        <div className="col-span-1 sm:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Card Front Image</label>
                            <button
                                type="button"
                                onClick={() => handleCameraCapture('frontimage')}
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center gap-2"
                            >
                                <i className="fas fa-camera"></i>
                                Capture Front Image
                            </button>
                            {newInsurance.frontimage && (
                                <img src={newInsurance.frontimage} alt="Front" className="mt-3 h-32 rounded-lg" />
                            )}
                        </div>
                        <div className="col-span-1 sm:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Card Back Image</label>
                            <button
                                type="button"
                                onClick={() => handleCameraCapture('backimage')}
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center gap-2"
                            >
                                <i className="fas fa-camera"></i>
                                Capture Back Image
                            </button>
                            {newInsurance.backimage && (
                                <img src={newInsurance.backimage} alt="Back" className="mt-3 h-32 rounded-lg" />
                            )}
                        </div>
                    </div>
                    <div className="flex gap-3 mt-6">
                        <button
                            type="button"
                            onClick={handleSaveInsurance}
                            className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded-lg"
                        >
                            Save Insurance
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowForm(false)}
                            className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-6 rounded-lg"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}


            {/* Insurance Cards */}
            <div className="space-y-2">
                {insurances.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-2">
                        {insurances.map((insurance, index) => (
                            <div
                                key={`${insurance.insuranceidnumber}-${index}`}
                                className={`border rounded-lg p-2 text-gray-800 relative transition-colors ${insurance.isDeleted
                                        ? 'border-red-400 bg-red-50'
                                        : 'border-gray-300 bg-white'
                                    }`}
                            >
                                {/* Action Buttons */}
                                <div className="absolute top-2 right-2 flex gap-2">
                                    {insurance.is_new && editingIndex !== index && (
                                        <button
                                            onClick={() => setEditingIndex(index)}
                                            className="text-blue-600 hover:text-blue-800 transition-colors"
                                            title="Edit Insurance"
                                            type="button"
                                        >
                                            <i className="fas fa-edit text-m"></i>
                                        </button>
                                    )}
                                    {insurance.is_new ? (
                                        <button
                                            onClick={() => handleDelete(index)}
                                            className="text-red-600 hover:text-red-800 transition-colors"
                                            title="Delete Insurance"
                                            type="button"
                                        >
                                            <i className="fas fa-trash text-m"></i>
                                        </button>
                                    ) : !insurance.isDeleted ? (
                                        <button
                                            onClick={() => handleDelete(index)}
                                            className="text-red-600 hover:text-red-800 transition-colors"
                                            title="Delete Insurance"
                                            type="button"
                                        >
                                            <i className="fas fa-trash text-m"></i>
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => handleUndo(index)}
                                            className="text-blue-600 hover:text-blue-800 transition-colors"
                                            title="Undo Delete"
                                            type="button"
                                        >
                                            <i className="fas fa-undo text-m"></i>
                                        </button>
                                    )}
                                </div>

                                {/* Edit Mode */}
                                {insurance.is_new && editingIndex === index ? (
                                    <div className="pt-8">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            <FormInput
                                                id={`policy-holder-edit-${index}`}
                                                label="Policy Holder Name"
                                                value={insurance.insurancepolicyholder}
                                                onChange={(e) => handleEditChange(e, index)}
                                                placeholder="Enter policy holder name"
                                                required
                                                labelPosition="top"
                                            />
                                            <DatePickerInput
                                                id={`dob-edit-${index}`}
                                                label="Date of Birth"
                                                value={insurance.insureddob ? new Date(insurance.insureddob) : null}
                                                onChange={(date) => {
                                                    const dateString = date ? date.toISOString().split('T')[0] : '';
                                                    const updated = [...insurances];
                                                    updated[index] = { ...updated[index], insureddob: dateString };
                                                    setInsurances(updated);
                                                }}
                                                required
                                            />
                                            <SelectField
                                                id={`gender-edit-${index}`}
                                                label="Gender"
                                                options={[
                                                    { value: 'M', label: 'Male' },
                                                    { value: 'F', label: 'Female' },
                                                    { value: 'Other', label: 'Other' }
                                                ]}
                                                value={insurance.insuredsex}
                                                onChange={(e) => {
                                                    const updated = [...insurances];
                                                    updated[index] = { ...updated[index], insuredsex: e.target.value };
                                                    setInsurances(updated);
                                                }}
                                                required
                                                labelPosition="top"
                                            />
                                            <SelectField
                                                id={`relationship-edit-${index}`}
                                                label="Relationship"
                                                options={[
                                                    { value: 'Self', label: 'Self' },
                                                    { value: 'Spouse', label: 'Spouse' },
                                                    { value: 'Child', label: 'Child' },
                                                    { value: 'Parent', label: 'Parent' },
                                                    { value: 'Other', label: 'Other' }
                                                ]}
                                                value={insurance.relationshiptoinsured}
                                                onChange={(e) => {
                                                    const updated = [...insurances];
                                                    updated[index] = { ...updated[index], relationshiptoinsured: e.target.value };
                                                    setInsurances(updated);
                                                }}
                                                required
                                                labelPosition="top"
                                            />
                                            <FormInput
                                                id={`plan-name-edit-${index}`}
                                                label="Insurance Plan Name"
                                                value={insurance.insuranceplanname}
                                                onChange={(e) => handleEditChange(e, index)}
                                                placeholder="Enter insurance plan name"
                                                required
                                                labelPosition="top"
                                            />
                                            <FormInput
                                                id={`id-number-edit-${index}`}
                                                label="Insurance ID Number"
                                                value={insurance.insuranceidnumber}
                                                onChange={(e) => handleEditChange(e, index)}
                                                placeholder="Enter insurance ID number"
                                                required
                                                labelPosition="top"
                                            />
                                            <div className="col-span-1 sm:col-span-2">
                                                <label className="block text-xs font-medium text-gray-700 mb-1">Card Front Image</label>
                                                <button
                                                    type="button"
                                                    onClick={() => handleEditCameraCapture(index, 'frontimage')}
                                                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-3 rounded-lg text-sm flex items-center justify-center gap-1"
                                                >
                                                    <i className="fas fa-camera text-xs"></i>
                                                    Capture Front
                                                </button>
                                                {insurance.frontimage && (
                                                    <img src={insurance.frontimage} alt="Front" className="mt-2 h-24 rounded-lg" />
                                                )}
                                            </div>
                                            <div className="col-span-1 sm:col-span-2">
                                                <label className="block text-xs font-medium text-gray-700 mb-1">Card Back Image</label>
                                                <button
                                                    type="button"
                                                    onClick={() => handleEditCameraCapture(index, 'backimage')}
                                                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-3 rounded-lg text-sm flex items-center justify-center gap-1"
                                                >
                                                    <i className="fas fa-camera text-xs"></i>
                                                    Capture Back
                                                </button>
                                                {insurance.backimage && (
                                                    <img src={insurance.backimage} alt="Back" className="mt-2 h-24 rounded-lg" />
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex gap-2 mt-4">
                                            <button
                                                type="button"
                                                onClick={handleSaveEdit}
                                                className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg text-sm"
                                            >
                                                Save
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setEditingIndex(null)}
                                                className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded-lg text-sm"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        {/* Insurance Details - Compact */}
                                        <div className="space-y-1 mb-2">
                                            <div>
                                                <p className="text-xs font-semibold text-gray-500">ID</p>
                                                <p className="text-xs font-mono font-semibold truncate">
                                                    {insurance.insuranceidnumber || 'N/A'}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-xs font-semibold text-gray-500">Plan</p>
                                                <p className="text-xs font-medium truncate">
                                                    {insurance.insuranceplanname || 'N/A'}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-xs font-semibold text-gray-500">Holder</p>
                                                <p className="text-xs font-medium truncate">
                                                    {insurance.insurancepolicyholder || 'N/A'}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-xs font-semibold text-gray-500">DOB</p>
                                                <p className="text-xs font-medium truncate">
                                                    {formatDate(insurance.insureddob)}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Card Images - Small */}
                                        {(insurance.frontimage || insurance.backimage) && (
                                            <div className="flex gap-1 pt-1 border-t border-gray-100">
                                                {insurance.frontimage && (
                                                    <div className="flex-1 min-w-0">
                                                        <img
                                                            src={insurance.frontimage}
                                                            alt="Insurance card front"
                                                            className="h-12 w-full rounded object-cover border border-gray-200"
                                                        />
                                                    </div>
                                                )}

                                                {insurance.backimage && (
                                                    <div className="flex-1 min-w-0">
                                                        <img
                                                            src={insurance.backimage}
                                                            alt="Insurance card back"
                                                            className="h-12 w-full rounded object-cover border border-gray-200"
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-sm text-gray-500">
                        Kindly add a new insurance using the Add New Insurance button.
                    </p>
                )}
            </div>



            {cameraMode && !cameraMode.startsWith('edit') && (
                <CameraCapture
                    onCapture={handleCaptureComplete}
                    onClose={() => {
                        setCameraMode(null);
                        setPendingImageType(null);
                    }}
                    imageType={pendingImageType}
                />
            )}

            {cameraMode && cameraMode.startsWith('edit') && (
                <CameraCapture
                    onCapture={(imageData) => {
                        const [_, indexStr, imageType] = cameraMode.split('-');
                        handleEditCaptureComplete(parseInt(indexStr), imageData);
                    }}
                    onClose={() => {
                        setCameraMode(null);
                        setPendingImageType(null);
                    }}
                    imageType={pendingImageType}
                />
            )}
        </div>
    );
};

export default Insurance;
